import React from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import styled from 'styled-components';
import { GOOGLE_CLIENT_ID } from '../../config/google';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Wrapper = styled.div<{ $disabled?: boolean }>`
  width: 100%;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};

  & > div {
    width: 100% !important;
  }

  iframe {
    width: 100% !important;
  }
`;

const FallbackButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--background, #fff);
  color: var(--muted-foreground, #64748b);
  font-size: 14px;
  cursor: not-allowed;
`;

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void | Promise<void>;
  onError?: (message: string) => void;
  disabled?: boolean;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onCredential,
  onError,
  disabled = false,
  text = 'continue_with',
}) => {
  if (!GOOGLE_CLIENT_ID) {
    return (
      <FallbackButton type="button" disabled title="Google sign-in is not configured">
        <GoogleIcon />
        Google sign-in unavailable
      </FallbackButton>
    );
  }

  const handleSuccess = (response: CredentialResponse) => {
    if (!response.credential) {
      onError?.('Google did not return a sign-in credential.');
      return;
    }
    void Promise.resolve(onCredential(response.credential)).catch((err: any) => {
      onError?.(err?.message || 'Google sign-in failed');
    });
  };

  return (
    <Wrapper $disabled={disabled}>
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={() => onError?.('Google sign-in was cancelled or failed.')}
        useOneTap={false}
        theme="outline"
        size="large"
        text={text}
        shape="rectangular"
        width="400"
        locale="en"
      />
    </Wrapper>
  );
};

export default GoogleSignInButton;
